import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import Datetime from 'react-datetime';
import moment from 'moment';

import ApplicationId from 'components/ApplicationId';
import Transition from 'components/Transition';
import Modal from 'components/Modal';
import Button from 'components/Button';
import Header from 'components/Header';
import Progress from 'components/Progress';
import FormRow from 'components/Form/FormRow';
import FormColumn from 'components/Form/FormColumn';
import FormGroup from 'components/Form/FormGroup';
import ContentWrapper from 'components/ContentWrapper';
import Checkbox from 'components/Form/Checkbox';
import Validator from 'components/Validator';
import Input from 'components/Form/Input';
import Select from 'components/Form/Select';
import CardNumberInput from '@lendingusa/react-common-card-number-input';
import imgError from 'assets/images/error.svg';
import editIcon from 'assets/images/edit.png';
import cancelEditIcon from 'assets/images/cancel-edit.png';

import {
  accountMask,
  routingNumberMask,
  currencyMask,
  numberUnmask,
} from 'utils/masks';
import { formatCurrency } from 'utils/formatCurrency';
import { getOrConvertToPst } from 'utils/timezone';
import { newNextAction } from '../../actions/navigation';

import schema from './debitCardSchema';
import './style.scss';

const DebitCardElection = ({
  validator: {
    values, errors, onChangeHandler, setValues, validate, setErrors, deleteErrors,
  },
  checkinData,
  navigate,
  dispatch,
  params,
  searchParams,
}) => {
  const [isProgressVisible, setIsProgressVisible] = useState(false);
  const [uploadingPercentage, setUploadingPercentage] = useState(0);
  const [firstPaymentDate, setFirstPaymentDate] = useState(getOrConvertToPst().add(37, 'd'));
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [isDatePickVisible, setIsDatePickVisible] = useState(false);
  const [isDisclosureVisible, setIsDisclosureVisible] = useState(false);
  const [isGiactPopupVisible, setIsGiactPopupVisible] = useState(false);
  const [giactPopupAttrs, setGiactPopupAttrs] = useState(null);
  const [giactMessage, setGiactMessage] = useState(null);
  const [isViewDetailsPopupVisible, setViewDetailsPopupVisible] = useState(false);
  const workflowType = get(params, 'workflowType');
  const key = searchParams.get('key');

  const paymentData = useRef({
    isBankAccountExisting: false,
    isDebitCardExisting: false,
  });

  useEffect(() => {
    const sourceData = get(checkinData, ['data', 'data']) || get(checkinData, ['state', 'data']);

    if (sourceData === undefined) {
      navigate(`${workflowType ? `/${workflowType}` : ''}/?key=${key}`);
    } else {
      const dataObj = get(checkinData, 'data.data', {}) || get(checkinData, 'state.data');
      const accounts = get(dataObj, 'bankAccounts', undefined);
      const primaryAccountIndex = (accounts || []).findIndex((acc) => acc.isPrimary);
      const payFromAccount = accounts ? accounts[primaryAccountIndex === -1 ? 0 : primaryAccountIndex] : get(dataObj, 'bankAccount', {});
      const debitCard = get(dataObj, 'debitCard');
      const error = get(checkinData, 'failure.errors.0', '');

      if (error) {
        setHasError(true);
        setErrorMessage(get(error, 'message'));
      }

      let initialBankData = {
        accounts,
        payFromAccount: JSON.stringify(payFromAccount),
        firstPaymentDate: getOrConvertToPst().add(37, 'd'),
        consentDate: getOrConvertToPst().format('MM/DD/YYYY'),
        isEditingDebitCard: false,
        isEditingBankInfo: false,
        debitCard: {
          accountHolderName: `${get(dataObj, 'applicant.firstName', '') || ''} ${get(dataObj, 'applicant.lastName', '') || ''}`,
        },
      };

      if (payFromAccount) {
        initialBankData = {
          ...initialBankData,
          accountId: get(payFromAccount, 'accountId'),
          autoBankAccount: {
            accountNumber: get(payFromAccount, 'accountNumber', '') || '',
            routingNumber: get(payFromAccount, 'routingNumber', '') || '',
            accountType: get(payFromAccount, 'accountType', 'Checking') || 'Checking',
            accountHolderName: get(payFromAccount, 'accountHolderName', '') || '',
            bankName: get(payFromAccount, 'bankName', '') || '',
          },
          isBankAccountExisting: true,
        };
        paymentData.current.isBankAccountExisting = true;
      } else {
        initialBankData.isBankAccountExisting = false;
        paymentData.current.isBankAccountExisting = false;
      }

      if (debitCard) {
        const expYear = get(debitCard, 'expirationDate.year', 0);
        const expMonth = get(debitCard, 'expirationDate.month', 0);

        initialBankData = {
          ...initialBankData,
          debitCard: {
            ...initialBankData.debitCard,
            cardNumber: get(debitCard, 'cardNumberLast4', '') || '',
            expirationDate: moment(`${expMonth}/${expYear}`, 'MM/YYYY').format('MM/YY'),
          },
          isDebitCardExisting: true,
        };
        paymentData.current.isDebitCardExisting = true;
      } else {
        initialBankData.isDebitCardExisting = false;
        paymentData.current.isDebitCardExisting = false;
      }

      if (get(dataObj, 'isAutoPay')) {
        initialBankData.isSelfSubmitted = get(dataObj, 'isDebitCard') ? 1 : 2;
      }

      setValues(initialBankData);
    }
  }, []);

  const handleSubmitForm = (flag = false, e) => {
    e.preventDefault();

    if (flag) {
      setIsGiactPopupVisible(false);
    }

    if (key) {
      if (schema && validate(schema).isValid) {
        setIsProgressVisible(true);

        const datePattern = 'MM/DD/YYYY';
        let payloadData = {
          isAutoPay: values.isSelfSubmitted === 1 || values.isSelfSubmitted === 2,
          firstPaymentDate: moment(values.firstPaymentDate).format(datePattern),
          iframe: 3,
        };

        if (values.isSelfSubmitted === 1) {
          payloadData = {
            ...payloadData,
            isDebitCard: true,
          };
        } else if (values.isSelfSubmitted === 2) {
          payloadData = {
            ...payloadData,
            isDebitCard: false,
          };
        } else if (values.isSelfSubmitted === 3) {
          payloadData = {
            ...payloadData,
            isDebitCard: false,
          };
        }

        if (flag) {
          payloadData = {
            ...payloadData,
            isDebitCard: false,
            isAutoPay: false,
          };
        }

        if (!paymentData.current.isDebitCardExisting || values.isEditingDebitCard) {
          const dateSplit = get(values, 'debitCard.expirationDate').split('/');
          const normalizedDate = `${dateSplit[0]}/20${dateSplit[1]}`;
          const expirationDateMoment = moment(normalizedDate, 'MM/YYYY');

          payloadData.DebitCard = {
            AccountHolderName: get(values, 'debitCard.accountHolderName', ''),
            CardNumber: numberUnmask(get(values, 'debitCard.cardNumber', '')),
            Expiration: {
              Month: expirationDateMoment.month() + 1,
              Year: expirationDateMoment.year(),
            },
          };
        }

        if (values.isSelfSubmitted === 2 && !flag) {
          if (!paymentData.current.isBankAccountExisting || values.isEditingBankInfo) {
            payloadData.AutoBankAccount = {
              AccountNumber: get(values, 'autoBankAccount.accountNumber', ''),
              RoutingNumber: get(values, 'autoBankAccount.routingNumber', ''),
              AccountType: get(values, 'autoBankAccount.accountType', ''),
              AccountHolderName: get(values, 'autoBankAccount.accountHolderName', ''),
              BankName: get(values, 'autoBankAccount.bankName', ''),
            };
          } else {
            payloadData.accountId = get(values, 'accountId');
          }
        }

        if (values.avsFailed) {
          payloadData.DebitCard = {
            ...(payloadData.DebitCard || {}),
            owner: {
              ...values.owner,
            },
          };
        }

        searchParams.get('isEmail') && Object.assign(payloadData, { isEmail: true });
        setHasError(false);
        setErrorMessage();
        setValues({ ...values });

        dispatch(newNextAction({
          key,
          workflowType: workflowType || 'text2apply',
          data: payloadData,
          onUploadProgress: (progressEvent) => {
            setUploadingPercentage(
              Math.floor((progressEvent.loaded * 100) / progressEvent.total)
            );
          },
          success: (res) => {
            const routeUrl = get(res, 'data.url');
            navigate(routeUrl);
          },
          fail: (err) => {
            setIsProgressVisible(false);

            if (get(err, 'response.data.failure.errors.0.subject') === 'bankAccountVerificationFailed') {
              setIsGiactPopupVisible(true);
              setGiactPopupAttrs(get(err, 'response.data.failure.errors.0.attributes'));
              setGiactMessage(get(err, 'response.data.failure.errors.0.message'));
            } else {
              setHasError(true);

              if (get(err, 'response.data.failure.errors.0')) {
                setErrorMessage(get(err, 'response.data.failure.errors.0.message'));

                if (get(err, 'response.data.failure.errors.0.subject') === 'avsFailed') {
                  setValues({ ...values, avsFailed: true });
                }
              } else {
                setErrorMessage('An unexpected error happened while saving the payment information. Please try again later.');
              }
            }
          },
        }));
      } else {
        validate(schema);
      }
    }
  };

  const prefillBankAccount = (accountIndex) => {
    const accounts = get(values, 'accounts', []);
    const bankData = accounts.find((acc) => acc.accountId === accountIndex);

    if (bankData) {
      setValues({
        ...values,
        accountId: get(bankData, 'accountId'),
        payFromAccount: JSON.stringify(bankData),
        autoBankAccount: {
          accountHolderName: get(bankData, 'accountHolderName') || '',
          bankName: get(bankData, 'bankName') || '',
          routingNumber: get(bankData, 'routingNumber') || '',
          accountNumber: get(bankData, 'accountNumber') || '',
          accountType: get(bankData, 'accountType') || '',
        },
      });
    }
  };

  const handleInputChange = (event) => {
    switch (event.target.name) {
      case 'accountId':
        prefillBankAccount(event.target.value);
        break;
      default:
        onChangeHandler(event.target.name, event.target.value);
    }
  };

  const toggleGiactPopup = (e) => {
    e.preventDefault();

    setIsGiactPopupVisible(false);
  };

  const handleConfirmDate = () => {
    setIsDatePickVisible(false);
    onChangeHandler('firstPaymentDate', firstPaymentDate);
  };

  const handleDateChange = (date) => {
    setFirstPaymentDate(date);
  };

  const toggleDebitCardEdit = () => {
    const dataObj = get(checkinData, 'data.data', {}) || get(checkinData, 'state.data');
    const debitCardData = get(dataObj, 'debitCard', null);

    if (!values.isEditingDebitCard) {
      setValues({
        ...values,
        debitCard: {
          ...values.debitCard,
          cardNumber: '',
          expirationDate: '',
        },
        isEditingDebitCard: !values.isEditingDebitCard,
      });
    } else {
      const expYear = get(debitCardData, 'expirationDate.year', 0);
      const expMonth = get(debitCardData, 'expirationDate.month', 0);

      setValues({
        ...values,
        debitCard: {
          ...values.debitCard,
          cardNumber: get(debitCardData, 'cardNumberLast4', ''),
          expirationDate: moment(`${expMonth}/${expYear}`, 'MM/YYYY').format('MM/YY'),
        },
        isEditingDebitCard: !values.isEditingDebitCard,
      });
    }
  };

  const toggleBankInfoEdit = () => {
    const payFromAccount = values.payFromAccount ? JSON.parse(values.payFromAccount) : null;

    if (!values.isEditingBankInfo) {
      setValues({
        ...values,
        autoBankAccount: {
          accountNumber: '',
          routingNumber: '',
          accountType: 'Checking',
          accountHolderName: '',
          bankName: '',
        },
        isEditingBankInfo: !values.isEditingBankInfo,
      });
    } else {
      setValues({
        ...values,
        autoBankAccount: {
          accountNumber: get(payFromAccount, 'accountNumber', ''),
          routingNumber: get(payFromAccount, 'routingNumber', ''),
          accountType: get(payFromAccount, 'accountType', 'Checking'),
          accountHolderName: get(payFromAccount, 'accountHolderName', ''),
          bankName: get(payFromAccount, 'bankName', ''),
        },
        isEditingBankInfo: !values.isEditingBankInfo,
      });
    }
  };

  const handleCheckboxChange = (name, value) => {
    onChangeHandler(name, value);

    if (name === 'isSelfSubmitted' && value === 2 && !values.autoBankAccount) {
      onChangeHandler('autoBankAccount.accountType', 'Checking');
      setValues({
        ...values,
        isSelfSubmitted: value,
        autoBankAccount: {
          ...(values.autoBankAccount || {}),
        },
      });
    } else {
      onChangeHandler(name, value);
    }
  };

  const handleFocus = () => {
    setIsDatePickVisible(true);
  };

  const handleCloseModal = () => {
    setIsDatePickVisible(false);
    setFirstPaymentDate(values.firstPaymentDate);
  };

  const handleClosePopup = () => {
    setIsDisclosureVisible(false);
  };

  const handleTrigger = () => {
    setIsDisclosureVisible(true);
  };

  const downPayment = get(checkinData, 'data.data.downPaymentRequired', 0);
  const { isDebitCardExisting, isBankAccountExisting } = paymentData.current;

  const [cardError, setCardError] = useState('');

  const handleCardChange = (digits) => {
    onChangeHandler('debitCard.cardNumber', digits);
  };

  const handleCardComplete = (digits, isValid) => {
    if (!isValid) {
      setErrors({ ...errors, 'debitCard.cardNumber': 'Invalid card number' });
    } else {
      deleteErrors(['debitCard.cardNumber']);
    }
  };

  const handleCardBlur = (digits, isValid) => {
    if (digits && !isValid) {
      setErrors({ ...errors, 'debitCard.cardNumber': 'Invalid card number' });
    }
  };

  const handleCardBrandChange = (brand, isAllowed) => {
    if (brand && !isAllowed) {
      setCardError('Currently we accept only Discover, Visa or Mastercard');
    } else {
      setCardError('');
    }
  };

  const handleCardCancel = () => {
    toggleDebitCardEdit();
  };

  const handleCardRevert = () => {
    toggleDebitCardEdit();
  };

  return (
    <section className="debit-card-election">
      <Transition className="transition-item wrapper v-center">
        <Header />
        <ApplicationId id={get(checkinData, 'attributes.entityId')} style={{ marginTop: 10 }} />
        <div className="header-section">
          <Button
            onClick={() => setViewDetailsPopupVisible(true)}
            className="view-details-btn"
            title="View Details"
            isDisabled={isProgressVisible}
          />
        </div>
        <ContentWrapper className="debit-card-election-content">
          <div className="debit-card-information">
            <div className="top-section">
              <div>
                <h2 className="title-header">Debit Card Information</h2>
                <p className="subtitle-header">(for down payment)</p>
                <p className="title-description">
                  By clicking Save &amp; Continue you authorize LendingUSA to process your down payment under these
                  &nbsp;
                  <span className="terms-link" onClick={handleTrigger}>terms</span>
                  .
                </p>
              </div>
              {isDebitCardExisting && (
                <div className="edit-container" onClick={toggleDebitCardEdit}>
                  <img src={values.isEditingDebitCard ? cancelEditIcon : editIcon} alt={values.isEditingDebitCard ? 'edit icon' : 'cancel edit icon'} />
                </div>
              )}
            </div>
            <div className="content-section">
              <FormRow>
                <FormColumn>
                  <FormGroup>
                    <Input
                      label="Down Payment Required"
                      name="downPayment"
                      value={`${formatCurrency(downPayment || 0, 2)}`}
                      isRequired
                      isMasked={currencyMask}
                      isBadgeVisible={false}
                      readOnly
                    />
                  </FormGroup>
                </FormColumn>
              </FormRow>
              <FormRow>
                <FormColumn>
                  <FormGroup>
                    <Input
                      label="Consent Date"
                      name="consentDate"
                      value={values.consentDate}
                      isBadgeVisible={false}
                      placeHolder="MM/DD/YYYY"
                      readOnly
                      isDisabled
                    />
                  </FormGroup>
                </FormColumn>
              </FormRow>
              <FormRow>
                <FormColumn>
                  <FormGroup>
                    <Input
                      label="Name"
                      name="debitCard.accountHolderName"
                      value={get(values, 'debitCard.accountHolderName', '') || ''}
                      onChange={handleInputChange}
                      hasError={!!get(errors, 'debitCard.accountHolderName')}
                      errorMessage={get(errors, 'debitCard.accountHolderName')}
                      isRequired
                      readOnly
                    />
                  </FormGroup>
                </FormColumn>
              </FormRow>
              <FormRow className="debit-card-container">
                <FormColumn offset={12} className="inputWrapper">
                  <CardNumberInput
                    className="card-number-input"
                    value={(!isDebitCardExisting || values.isEditingDebitCard) ? (get(values, 'debitCard.cardNumber') || null) : null}
                    lastFour={isDebitCardExisting && !values.isEditingDebitCard ? (get(values, 'debitCard.cardNumber') || null) : null}
                    allowedBrands={['visa', 'mastercard', 'discover']}
                    onChange={handleCardChange}
                    onComplete={handleCardComplete}
                    onBlur={handleCardBlur}
                    onBrandChange={handleCardBrandChange}
                    onCancel={isDebitCardExisting ? handleCardCancel : undefined}
                    onRevert={isDebitCardExisting ? handleCardRevert : undefined}
                    disabled={!values.isEditingDebitCard && isDebitCardExisting}
                    error={!!(get(errors, 'debitCard.cardNumber') || cardError)}
                    label="Debit Card Number"
                    placeholder=""
                    isRequired
                  />
                  {(get(errors, 'debitCard.cardNumber') || cardError) && (
                    <span className="error">{get(errors, 'debitCard.cardNumber') || cardError}</span>
                  )}
                  {!cardError && !get(errors, 'debitCard.cardNumber') && <small>Discover, Visa or Mastercard only</small>}
                </FormColumn>
              </FormRow>
              <FormRow>
                <FormColumn>
                  <Input
                    name="debitCard.expirationDate"
                    label="Expiration Date"
                    onChange={handleInputChange}
                    value={get(values, 'debitCard.expirationDate') || ''}
                    isMasked={[/\d/, /\d/, '/', /\d/, /\d/]}
                    isRequired
                    hasError={!!get(errors, 'debitCard.expirationDate')}
                    errorMessage={get(errors, 'debitCard.expirationDate')}
                    placeHolder="MM/YY"
                    readOnly={!values.isEditingDebitCard && isDebitCardExisting}
                    inputmode="numeric"
                  />
                </FormColumn>
              </FormRow>
              {hasError && (
                <FormRow>
                  <FormColumn>
                    <div className="alert-container">
                      {errorMessage || 'The payment format used did not go through. Please try again or use another method.'}
                    </div>
                  </FormColumn>
                </FormRow>
              )}
              <div className="enroll-section">
                <FormRow>
                  <FormColumn>
                    <div>
                      <h2 className="title-header">Enroll in AutoPay?</h2>
                    </div>
                  </FormColumn>
                </FormRow>
                <FormRow className="mb-1">
                  <FormColumn>
                    <Checkbox
                      containerClass={(values.isSelfSubmitted === 1 || !values.isSelfSubmitted) && 'selected'}
                      name="isSelfSubmitted"
                      label="Yes - Debit Card"
                      onChange={handleCheckboxChange.bind(null, 'isSelfSubmitted', 1)}
                      isChecked={values.isSelfSubmitted === 1}
                      errorMessage
                    />
                  </FormColumn>
                  <FormColumn>
                    <Checkbox
                      containerClass={(values.isSelfSubmitted === 2) && 'selected'}
                      name="isSelfSubmitted"
                      label="Yes - ACH"
                      onChange={handleCheckboxChange.bind(null, 'isSelfSubmitted', 2)}
                      isChecked={values.isSelfSubmitted === 2}
                      errorMessage
                    />
                  </FormColumn>
                </FormRow>
                <FormRow>
                  <FormColumn>
                    <Checkbox
                      containerClass={(values.isSelfSubmitted === 3) && 'selected'}
                      name="isSelfSubmitted"
                      label="No AutoPay"
                      onChange={handleCheckboxChange.bind(null, 'isSelfSubmitted', 3)}
                      isChecked={values.isSelfSubmitted === 3}
                      errorMessage
                    />
                  </FormColumn>
                </FormRow>
                {errors.isSelfSubmitted ? (
                  <FormRow>
                    <FormColumn>
                      <span className="error">{errors.isSelfSubmitted}</span>
                    </FormColumn>
                  </FormRow>
                ) : null}
              </div>
              {
                values.isSelfSubmitted === 2 && (
                  <>
                    <div className="bank-account-section">
                      <h2 className="title-header">Bank ACH Information</h2>
                      {isBankAccountExisting && (
                        <div className="edit-container" onClick={toggleBankInfoEdit}>
                          <img src={values.isEditingBankInfo ? cancelEditIcon : editIcon} alt={values.isEditingDebitCard ? 'edit icon' : 'cancel edit icon'} />
                        </div>
                      )}
                    </div>
                    {values.accounts && values.accounts.length > 1 && !values.isEditingBankInfo ? (
                      <FormRow>
                        <FormColumn>
                          <Select
                            data={values.accounts.map((acc) => ({
                              value: acc.accountId, title: `${acc.accountType} #${acc.accountNumber.slice(-4)}`,
                            }))}
                            label="Pay from Account"
                            placeholder="Select..."
                            name="accountId"
                            onChange={handleInputChange}
                            isRequired
                            isErrorVisible={false}
                            value={get(values, 'accountId', '')}
                          />
                        </FormColumn>
                      </FormRow>
                    ) : null}
                    <FormRow>
                      <FormColumn>
                        <FormGroup>
                          <Input
                            label="Bank Name"
                            name="autoBankAccount.bankName"
                            value={get(values, 'autoBankAccount.bankName', '') || ''}
                            onChange={handleInputChange}
                            hasError={!!get(errors, 'autoBankAccount.bankName')}
                            errorMessage={get(errors, 'autoBankAccount.bankName')}
                            readOnly={!values.isEditingBankInfo && isBankAccountExisting}
                            isRequired
                          />
                        </FormGroup>
                      </FormColumn>
                    </FormRow>
                    <FormRow>
                      <FormColumn>
                        <FormGroup>
                          <Input
                            label="Name on Account"
                            name="autoBankAccount.accountHolderName"
                            value={get(values, 'autoBankAccount.accountHolderName', '') || ''}
                            onChange={handleInputChange}
                            hasError={!!get(errors, 'autoBankAccount.accountHolderName')}
                            errorMessage={get(errors, 'autoBankAccount.accountHolderName')}
                            readOnly={!values.isEditingBankInfo && isBankAccountExisting}
                            isRequired
                          />
                        </FormGroup>
                      </FormColumn>
                    </FormRow>
                    <FormRow>
                      <FormColumn>
                        <FormGroup>
                          <Input
                            type="tel"
                            label="Routing Number"
                            name="autoBankAccount.routingNumber"
                            value={get(values, 'autoBankAccount.routingNumber', '') || ''}
                            onChange={handleInputChange}
                            isMasked={(values.isEditingBankInfo || !isBankAccountExisting) ? routingNumberMask : undefined}
                            hasError={!!get(errors, 'autoBankAccount.routingNumber')}
                            errorMessage={get(errors, 'autoBankAccount.routingNumber')}
                            readOnly={!values.isEditingBankInfo && isBankAccountExisting}
                            isRequired
                          />
                        </FormGroup>
                      </FormColumn>
                    </FormRow>
                    <FormRow>
                      <FormColumn>
                        <FormGroup>
                          <Input
                            type="tel"
                            label="Account Number"
                            name="autoBankAccount.accountNumber"
                            value={get(values, 'autoBankAccount.accountNumber', '') || ''}
                            onChange={handleInputChange}
                            isMasked={(values.isEditingBankInfo || !isBankAccountExisting) ? accountMask : undefined}
                            hasError={!!get(errors, 'autoBankAccount.accountNumber')}
                            errorMessage={get(errors, 'autoBankAccount.accountNumber')}
                            readOnly={!values.isEditingBankInfo && isBankAccountExisting}
                            isRequired
                          />
                        </FormGroup>
                      </FormColumn>
                    </FormRow>
                    <FormRow>
                      <FormColumn>
                        <FormGroup>
                          <Select
                            label="Account Type"
                            name="autoBankAccount.accountType"
                            data={[
                              { value: 'Checking', title: 'Checking' },
                              { value: 'Savings', title: 'Savings' },
                            ]}
                            value={get(values, 'autoBankAccount.accountType', '')}
                            onChange={handleInputChange}
                            hasError={!!get(errors, 'autoBankAccount.accountType')}
                            errorMessage={get(errors, 'autoBankAccount.accountType')}
                            isDisabled={!values.isEditingBankInfo && isBankAccountExisting}
                            className="readOnly"
                            isRequired
                          />
                        </FormGroup>
                      </FormColumn>
                    </FormRow>
                  </>
                )
              }
              <FormRow className="payroll-date">
                <FormColumn>
                  <h3>Select a date which matches your payroll date</h3>
                  <Input
                    label="First Payment Date"
                    name="firstPaymentDate"
                    value={values.firstPaymentDate ? `${values.firstPaymentDate.format('MM/DD/YYYY')}` : ''}
                    hasError={errors.firstPaymentDate}
                    onFocus={handleFocus}
                    isRequired
                    readOnly
                  />
                </FormColumn>
              </FormRow>
              <FormRow className="save-and-continue">
                <FormColumn>
                  <Button
                    onClick={(e) => handleSubmitForm(false, e)}
                    className="btn btn-primary save-button"
                    title="Save And Continue"
                    isDisabled={isProgressVisible}
                  />
                </FormColumn>
              </FormRow>
            </div>
          </div>
        </ContentWrapper>
      </Transition>
      {
        isProgressVisible && <Progress percent={uploadingPercentage} />
      }
      {
        isGiactPopupVisible && (
          <Modal>
            <div className="card-content error-modal security-error">
              <img src={imgError} alt="" />
              <p>{giactMessage}</p>
              {giactPopupAttrs && giactPopupAttrs.allowRetry && (
                <div onClick={toggleGiactPopup} className="btn btn-red bottomMargin">TRY AGAIN</div>
              )}
              {giactPopupAttrs && giactPopupAttrs.allowToProceedWithoutAutopay && (
                <div onClick={(e) => handleSubmitForm(true, e)} className={!giactPopupAttrs.allowRetry ? 'btn btn-red' : 'btn btn-blue'}>Proceed without signing up with autopay</div>
              )}
            </div>
          </Modal>
        )
      }
      {
        isDatePickVisible && (
          <Modal>
            <Datetime
              input={false}
              className="dateWrapper"
              initialValue={values.firstPaymentDate}
              value={firstPaymentDate}
              displayTimeZone="America/Los_Angeles"
              isValidDate={(current) => (
                current.isAfter(Datetime.moment().add(36, 'd'))
                && current.isBefore(Datetime.moment().add(47, 'd'))
              )}
              onChange={handleDateChange}
              dateFormat="MM/DD/YYYY"
              timeFormat={false}
            />
            <div style={{ padding: 10, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                onClick={handleCloseModal}
                className="btn btn-md btn-blue-outline"
                title="Close"
              />
              <Button
                onClick={handleConfirmDate}
                className="btn btn-md btn-blue"
                title="Confirm"
              />
            </div>
          </Modal>
        )
      }
      {
        isDisclosureVisible && (
          <Modal>
            <div className="card-content-definition">
              <div className="card-header">
                <h5>View Details</h5>
              </div>
            </div>
            <div className="close" onClick={handleClosePopup} />
            <div className="card-content diclosure-card">
              You represent and warrant that you are authorized to use the debit card you have provided for the down payment on your purchase from Merchant (“Debit Card”). You are authorizing Cross River Bank, as agent of Merchant, to charge your Debit Card in the amount of the down payment.
              <br />
              You understand that your Debit Card may be charged prior to you signing your loan agreement. If you do not sign your loan agreement within thirty (30) days of the date that you applied for this loan, your loan offer will expire and the lender will have no obligation to provide you the loan. In that event, Merchant will refund the down payment to you by electronic funds transfer to your bank. If you cancel your purchase with Merchant prior to signing your loan agreement, you can request a refund of the down payment made to Merchant by calling (800) 994-6177 ext. 0.
            </div>
          </Modal>
        )
      }
      {
        isViewDetailsPopupVisible && (
          <Modal>
            <div className="card-content-definition">
              <div className="card-header">
                <h5>View Details</h5>
              </div>
            </div>
            <div className="close" onClick={() => setViewDetailsPopupVisible(false)} />
            <div className="card-content diclosure-card">
              *This account credit (“Credit”) will either be applied as a $25 statement credit or as a $25 principal reduction. Please review your ‘Electronic Funds Transfer Authorization’ at origination to confirm how it will be applied. To qualify for this Credit promotion (“Promotion”), you must enroll in optional Auto-Pay (“Auto-Pay”) at the same time that you execute your loan agreement with our
              &nbsp;
              <a href="https://lendingusa.com/lending-partners/" target="_blank" rel="noreferrer">lending partners</a>
              &nbsp;
              and successfully complete your first three (3) scheduled monthly payments using Auto-Pay. Each monthly payment must post to your loan account prior to the expiration of any applicable grace period as defined in your loan agreement. After your first three (3) scheduled monthly Auto-Pay payments successfully post to your loan account, the Credit will reflect on your next scheduled loan statement. You understand that you are not required to enroll in Auto-Pay unless you want to take advantage of this promotional offer, and your willingness to enroll in Auto-Pay will not affect our
              &nbsp;
              <a href="https://lendingusa.com/lending-partners/" target="_blank" rel="noreferrer">lending partners'</a>
              &nbsp;
              credit decision regarding your loan application. Limit one Credit per loan. Promotion cannot be transferred or assigned. Enrollment for the Statement Credit/Promotion must be made before December 31st, 2024. This Promotion is subject to change or discontinuation without notice.
            </div>
          </Modal>
        )
      }
    </section>
  );
};

DebitCardElection.propTypes = {
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  checkinData: PropTypes.object.isRequired,
  validator: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
  searchParams: PropTypes.object.isRequired,
};

export default Validator(schema)(connect((state) => ({
  fetch: state.fetch,
  checkinData: state.screen.checkinData,
}))(DebitCardElection));
